import type { CSSProperties, Ref } from 'react';
import { useState } from 'react';
import type { AvatarSize, AvatarShape } from './Avatar.css';
import { root, initialsSize, image, sizePx } from './Avatar.css';
import { getInitials } from './getInitials';

export type { AvatarSize, AvatarShape };

export interface AvatarProps {
  /** Ref to the root element */
  ref?: Ref<HTMLSpanElement>;
  /** Display name — used for the image alt text, the initials fallback, and the accessible label */
  name: string;
  /** Image URL. Falls back to initials when absent or on load error. */
  src?: string;
  /** Named size preset. Ignored when `size` (px) is provided. */
  avatarSize?: AvatarSize;
  /** Explicit pixel size override */
  size?: number;
  /** Shape of the avatar */
  shape?: AvatarShape;
  /** Background color for the initials fallback (e.g. a per-user tint) */
  tint?: string;
  /** Custom class name */
  className?: string;
}

export function Avatar({
  name,
  src,
  avatarSize = 'md',
  size,
  shape = 'circle',
  tint,
  className,
  ref,
}: AvatarProps) {
  const [errored, setErrored] = useState(false);
  const showImage = !!src && !errored;

  const px = size ?? sizePx[avatarSize];
  const dimensionStyle: CSSProperties = { width: px, height: px };

  return (
    <span
      ref={ref}
      className={`${root({ shape })} ${initialsSize[avatarSize]}${className ? ` ${className}` : ''}`}
      style={showImage ? dimensionStyle : { ...dimensionStyle, background: tint }}
      role={showImage ? undefined : 'img'}
      aria-label={showImage ? undefined : name}
    >
      {showImage ? (
        <img className={image} src={src} alt={name} onError={() => setErrored(true)} />
      ) : (
        <span aria-hidden="true">{getInitials(name)}</span>
      )}
    </span>
  );
}
